import { motion } from "framer-motion";
import { toast } from "sonner";
import { Copy, ExternalLink, Phone } from "lucide-react";
import { PAYMENTS } from "@/config/site";

const copyTag = (text, label) => {
  navigator.clipboard.writeText(text);
  toast.success(`${label} copied`);
};

export default function Payments() {
  return (
    <section id="payments" className="relative bg-[#0A0A0A] py-20 md:py-32 border-t border-[#1a1a1a]">
      <div className="max-w-7xl mx-auto px-5 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-mono text-xs tracking-[0.35em] text-[#E60000] uppercase">
            Four ways to pay. Zero hassle.
          </p>
          <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl mt-3 text-white">
            Pay your way
          </h2>
          <p className="font-mono text-sm text-[#888] mt-4 max-w-2xl leading-relaxed">
            Place your order, get a 6-character verification code by email, pay with your favorite
            app, then enter the code to verify. We match your payment to your order email.
          </p>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PAYMENTS.map((m, i) => (
            <motion.div
              key={m.id}
              data-testid={`payment-card-${m.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="bg-[#121212] border border-[#262626] hover:border-[#E60000]/60 transition-colors p-6 flex flex-col"
            >
              <p className="font-mono text-[10px] tracking-[0.3em] text-[#666]">
                0{i + 1}
              </p>
              <h3 className="font-display uppercase text-2xl text-white mt-2">{m.name}</h3>
              <p className="font-mono text-sm text-[#E60000] mt-1">
                {m.id === "visa" ? m.phone : m.tag}
              </p>
              <p className="font-mono text-[11px] text-[#888] mt-3 leading-relaxed flex-1">
                {m.note}
              </p>
              {m.qr && (
                <div className="mt-4 border border-[#262626] bg-white p-3">
                  <img
                    src={m.qr}
                    alt="PayPal QR code - scan to pay isaac brenna"
                    data-testid="paypal-qr-image"
                    className="w-full"
                  />
                  <p className="font-mono text-[10px] tracking-[0.15em] text-black text-center mt-2 uppercase">
                    Scan to pay isaac brenna
                  </p>
                </div>
              )}
              <div className="mt-5 flex gap-2">
                {m.url ? (
                  <a
                    href={m.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`payment-link-${m.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-black font-mono text-xs tracking-[0.15em] uppercase px-3 py-3 hover:bg-[#E60000] hover:text-white transition-colors"
                  >
                    Pay <ExternalLink size={14} />
                  </a>
                ) : m.qr ? null : m.phone ? (
                  <span
                    data-testid="visa-phone-number"
                    className="flex-1 inline-flex items-center justify-center gap-2 border border-[#333] text-white font-mono text-xs tracking-[0.15em] uppercase px-3 py-3"
                  >
                    <Phone size={14} /> {m.phone}
                  </span>
                ) : (
                  <span
                    data-testid="card-at-checkout-note"
                    className="flex-1 inline-flex items-center justify-center gap-2 border border-[#333] text-white font-mono text-xs tracking-[0.15em] uppercase px-3 py-3"
                  >
                    At checkout
                  </span>
                )}
                <button
                  onClick={() => copyTag(m.id === "visa" ? m.phone : m.tag, m.name)}
                  data-testid={`copy-tag-${m.id}`}
                  aria-label={`Copy ${m.name} details`}
                  className="border border-[#333] text-white px-3 py-3 hover:border-[#E60000] hover:text-[#E60000] transition-colors"
                >
                  <Copy size={14} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
